import TimeIcon from "@mui/icons-material/AccessTimeRounded";
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import type { PropsWithChildren } from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { convertISODateShort } from "@entrilia/shared/utilities/dateUtils";
import type { ChangeRequestDetails } from "../../../../api/types/changeRequestsTypes";
import { identityUserIdSelector } from "../../../../store/state/user/selectors";
import ChangeRequestAttachmentsList from "./ChangeRequestAttachmentsList";
import ConfirmChangeRequestCancellationDialog from "./ConfirmChangeRequestCancellationDialog";

interface Props {
  open: boolean;
  onClose: () => void;
  canUserEdit: boolean;
  investorId: string;
  changeRequestDetails: ChangeRequestDetails;
  onChangeRequestCanceled: () => void;
}

const PendingChangeRequestDialog = ({
  open,
  onClose,
  canUserEdit,
  investorId,
  changeRequestDetails,
  onChangeRequestCanceled,
  children,
}: PropsWithChildren<Props>) => {
  const userId = useSelector(identityUserIdSelector);
  const [showCancelConfirmationDialog, setShowCancelConfirmationDialog] = useState(false);

  const handleChangeRequestCanceled = () => {
    setShowCancelConfirmationDialog(false);
    onChangeRequestCanceled();
  };

  const isRequestedByCurrentUser = changeRequestDetails.requestedByUserId === userId;

  return (
    <>
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle>Request (#{changeRequestDetails.referenceCode})</DialogTitle>
        <DialogCloseButton onClick={onClose} />
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ width: "100%", minHeight: "35vh" }}>
            <Alert
              severity="warning"
              iconMapping={{ warning: <TimeIcon fontSize="inherit" /> }}
              sx={{ position: "relative" }}
            >
              <AlertTitle>Pending Changes (#{changeRequestDetails.referenceCode})</AlertTitle>
              {canUserEdit && isRequestedByCurrentUser && (
                <Button
                  variant="text"
                  color="inherit"
                  sx={{ position: "absolute", right: 12, top: 12, py: 0 }}
                  onClick={() => setShowCancelConfirmationDialog(true)}
                >
                  Cancel Request
                </Button>
              )}
              <Typography>
                Change request submitted by <strong>{changeRequestDetails.requestedByUserName}</strong> on{" "}
                <strong>{convertISODateShort(changeRequestDetails.requestedAt)}</strong> is waiting for approval.
              </Typography>
            </Alert>
            {children}
            {changeRequestDetails.attachments.length > 0 && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle1">Attachments</Typography>
                <ChangeRequestAttachmentsList investorId={investorId} attachments={changeRequestDetails.attachments} />
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <Divider />
        <DialogButtons>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </DialogButtons>
      </Dialog>
      <ConfirmChangeRequestCancellationDialog
        investorId={investorId}
        workflowId={changeRequestDetails.workflowId}
        referenceCode={changeRequestDetails.referenceCode}
        open={showCancelConfirmationDialog}
        onClose={() => setShowCancelConfirmationDialog(false)}
        onChangeRequestCanceled={handleChangeRequestCanceled}
      />
    </>
  );
};

export default PendingChangeRequestDialog;
